import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ConnectionFilesTable from 'instruments/connection-files/ConnectionFilesTable';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {FileListingGraphQL} from '../../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionDataQuery, ConnectionDataQueryVariables} from './__generated/ConnectionDataQuery';

export interface ConnectionDataProps {
  connectionId: string;
  dataCatalogFolderId: string | null;
}

function ConnectionData(props: ConnectionDataProps): JSX.Element {
  const [files, setFiles] = useState<Array<FileListingGraphQL>>([]);
  const connectionIds = useMemo(() => [props.connectionId], [props.connectionId]);

  return (
    <div className="react-tab-container">
      <ConnectionFilesTable
        connectionFiles={files}
        setConnectionFiles={setFiles}
        columns={['File name', 'Status', 'Modified at', 'Created at']}
        connectionIds={connectionIds}
        hideCheckboxColumn={true}
        onSelectRow={(rowIndex) => {
          const selectedFile = files[rowIndex];
          LoadingScreen.showFileLoading();
          FileBrowserActions.openFile(selectedFile.id);
        }}
      />
    </div>
  );
}

export const CONNECTION_DATA_QUERY: DocumentNode = gql`
  query ConnectionDataQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        dataCatalogFolder {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingConnectionData(props: {connectionId: string}): JSX.Element {
  const result = useBenchlingQuery<ConnectionDataQuery, ConnectionDataQueryVariables>(CONNECTION_DATA_QUERY, {
    variables: {connectionId: props.connectionId},
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return <LoadingFileScreen />;
  }
  if (isInaccessible(result.data.connection)) {
    return (
      <WorkspaceItemInaccessibleGuard inaccessibleInfo={result.data.connection} modelType="connection" />
    );
  } else {
    return (
      <ConnectionData
        connectionId={props.connectionId}
        dataCatalogFolderId={result.data.connection.dataCatalogFolder?.id ?? null}
      />
    );
  }
}
